import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard,
  Users,
  UserCog,
  GraduationCap,
  CalendarCheck,
  Wallet,
  DoorOpen,
  PhoneCall,
  BookOpen,
  ClipboardList,
  Library,
  Boxes,
  Bus,
  MessageSquare,
  BarChart3,
  Settings,
  ChevronDown,
  LogOut,
  Menu,
  X,
  Plus,
  Minus,
  Palette,
  Trophy,
  KeyRound,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import ChangePasswordDialog from "@/components/common/ChangePasswordDialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { THEMES } from "@/lib/themes";
import {
  findRolePermissionEntry,
  hasModuleAccess,
  loadRolePermissions,
  normalizePermissionKey,
} from "@/lib/permissions";

const MODULES = [
  { key: "dashboard", path: "/", label: "Dashboard", icon: LayoutDashboard },
  { key: "visitor", path: "/visitor", label: "Visitor", icon: DoorOpen },
  {
    key: "front-office",
    path: "/front-office",
    label: "Front Office",
    icon: PhoneCall,
  },
  {
    key: "students",
    path: "/students",
    label: "Students",
    icon: GraduationCap,
  },
  {
    key: "attendance",
    path: "/attendance",
    label: "Attendance",
    icon: CalendarCheck,
  },
  { key: "fees", path: "/fees", label: "Fees", icon: Wallet },
  { key: "staff", path: "/staff", label: "Staff", icon: UserCog },
  { key: "academic", path: "/academic", label: "Academic", icon: BookOpen },
  { key: "calendar", path: "/calendar", label: "Calendar", icon: Calendar },
  { key: "allotment", path: "/allotment", label: "Allotment", icon: Trophy },
  {
    key: "lesson-plan",
    path: "/lesson-plan",
    label: "Lesson Plan",
    icon: ClipboardList,
  },
  {
    key: "assignment",
    path: "/assignment",
    label: "Assignment",
    icon: ClipboardList,
  },
  { key: "library", path: "/library", label: "Library", icon: Library },
  { key: "inventory", path: "/inventory", label: "Inventory", icon: Boxes },
  { key: "transport", path: "/transport", label: "Transport", icon: Bus },
  {
    key: "communicate",
    path: "/communicate",
    label: "Communicate",
    icon: MessageSquare,
  },
  { key: "reports", path: "/reports", label: "Reports", icon: BarChart3 },
  { key: "settings", path: "/settings", label: "Settings", icon: Settings },
];

function toPermissionKey(moduleKey) {
  return normalizePermissionKey(moduleKey);
}

export default function AppShell() {
  const {
    user,
    logout,
    branchId,
    sessionId,
    updateBranch,
    updateSession,
    themeKey,
    updateTheme,
    isSuperAdmin,
  } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [fontSize, setFontSize] = useState(
    parseInt(localStorage.getItem("erp_font") || "13"),
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const megaNavRef = useRef(null);
  const [autoScrollDir, setAutoScrollDir] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [sessionConfirmOpen, setSessionConfirmOpen] = useState(false);
  const [pendingSessionId, setPendingSessionId] = useState("");
  const [schoolName, setSchoolName] = useState("School ERP");
  const [modulesEnabled, setModulesEnabled] = useState({});
  const [rolePermissions, setRolePermissions] = useState({});

  const loadContextOptions = useCallback(async () => {
    try {
      const [b, s] = await Promise.all([
        api.get("/branches"),
        api.get("/sessions"),
      ]);
      const bItems = b.data.items || [];
      const sItems = s.data.items || [];
      setBranches(bItems);
      setSessions(sItems);

      const targetBranchId = branchId || bItems[0]?.id || "";
      try {
        const cfg = await api.get("/settings/school_info", {
          params: { branch_id: targetBranchId || undefined },
        });
        const name =
          cfg?.data?.value?.school_name ||
          cfg?.data?.school_name ||
          "School ERP";
        setSchoolName(String(name || "School ERP"));
      } catch {
        setSchoolName("School ERP");
      }

      if (
        (!branchId || !bItems.find((x) => x.id === branchId)) &&
        bItems[0]?.id
      ) {
        updateBranch(bItems[0].id);
      }
      if (
        (!sessionId || !sItems.find((x) => x.id === sessionId)) &&
        sItems[0]?.id
      ) {
        updateSession(sItems[0].id);
      }
    } catch {}
  }, [branchId, sessionId, updateBranch, updateSession]);

  useEffect(() => {
    loadContextOptions();
  }, [loadContextOptions]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (isSuperAdmin) {
        if (!cancelled) {
          setModulesEnabled({});
          setRolePermissions({});
        }
        return;
      }

      const [modulesRes, permsRes] = await Promise.allSettled([
        api.get("/settings/modules_enabled"),
        loadRolePermissions(api),
      ]);

      if (cancelled) return;

      const modulesValue =
        modulesRes.status === "fulfilled" &&
        modulesRes.value?.data &&
        typeof modulesRes.value.data.value === "object"
          ? modulesRes.value.data.value
          : {};

      const permsValue =
        permsRes.status === "fulfilled" &&
        permsRes.value &&
        typeof permsRes.value === "object"
          ? permsRes.value
          : {};

      setModulesEnabled(modulesValue || {});
      setRolePermissions(permsValue || {});
    })();

    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, user?.role]);

  const rolePermissionEntry = useMemo(
    () => findRolePermissionEntry(rolePermissions, user?.role),
    [rolePermissions, user?.role],
  );

  const visibleModules = useMemo(() => {
    return MODULES.filter((moduleItem) => {
      if (isSuperAdmin) return true;

      if (moduleItem.key === "settings") {
        return false;
      }

      const moduleToggleKey = toPermissionKey(moduleItem.key);
      if (
        modulesEnabled &&
        Object.keys(modulesEnabled).length > 0 &&
        modulesEnabled[moduleToggleKey] === false
      ) {
        return false;
      }

      if (rolePermissionEntry == null) {
        return moduleItem.key === "dashboard";
      }

      return hasModuleAccess(rolePermissionEntry, moduleItem.key, isSuperAdmin);
    });
  }, [isSuperAdmin, modulesEnabled, rolePermissionEntry]);

  useEffect(() => {
    const onSessionUpdate = () => {
      loadContextOptions();
    };
    window.addEventListener("erp-sessions-updated", onSessionUpdate);
    return () =>
      window.removeEventListener("erp-sessions-updated", onSessionUpdate);
  }, [loadContextOptions]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--erp-font-size",
      `${fontSize}px`,
    );
    localStorage.setItem("erp_font", String(fontSize));
  }, [fontSize]);

  const syncMegaNavScrollState = useCallback(() => {
    const el = megaNavRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 2;
    setCanScrollLeft(hasOverflow && el.scrollLeft > 2);
    setCanScrollRight(
      hasOverflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
    );
  }, []);

  useEffect(() => {
    syncMegaNavScrollState();
    const el = megaNavRef.current;
    if (!el) return;

    const onScroll = () => syncMegaNavScrollState();
    const onResize = () => syncMegaNavScrollState();

    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [syncMegaNavScrollState]);

  useEffect(() => {
    if (!autoScrollDir) return;
    const el = megaNavRef.current;
    if (!el) return;

    const timer = window.setInterval(() => {
      el.scrollBy({ left: autoScrollDir * 14, behavior: "auto" });
      syncMegaNavScrollState();
    }, 16);

    return () => window.clearInterval(timer);
  }, [autoScrollDir, syncMegaNavScrollState]);

  const branch = branches.find((b) => b.id === branchId);
  const session = sessions.find((s) => s.id === sessionId);

  return (
    <div
      className="erp-root min-h-screen text-slate-900"
      style={{ fontSize: `${fontSize}px`, background: "var(--erp-background)" }}
    >
      {/* Top bar — compact, theme-aware */}
      <header
        className="h-12 erp-bg-topbar flex items-center px-2 md:px-3 gap-2 md:gap-3 sticky top-0 z-50 shadow-sm"
        data-testid="top-bar"
      >
        {/* Mobile hamburger */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <button
              className="lg:hidden w-8 h-8 rounded hover:bg-white/10 flex items-center justify-center"
              data-testid="mobile-menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72">
            <div className="erp-gradient text-white px-5 py-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center text-white font-bold border border-white/30">
                  S
                </div>
                <div>
                  <div className="font-bold text-sm font-serif-display">
                    {schoolName || "School ERP"}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider opacity-80">
                    {branch?.name || ""}
                  </div>
                </div>
              </div>
            </div>
            <nav
              className="overflow-y-auto"
              style={{ maxHeight: "calc(100vh - 80px)" }}
            >
              {visibleModules.map((m) => {
                const Icon = m.icon;
                return (
                  <NavLink
                    key={m.key}
                    to={m.path}
                    end={m.path === "/"}
                    data-testid={`mnav-${m.key}`}
                    onClick={() => setDrawerOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-4 ${
                        isActive
                          ? "border-[var(--erp-accent)] erp-text-primary bg-slate-50"
                          : "border-transparent text-slate-600 hover:bg-slate-50"
                      }`
                    }
                  >
                    <Icon className="w-4 h-4" />
                    {m.label}
                  </NavLink>
                );
              })}
            </nav>
          </SheetContent>
        </Sheet>

        {/* Brand */}
        <div
          className="flex items-center gap-2 font-bold text-[15px]"
          data-testid="brand"
        >
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center font-bold text-sm"
            style={{
              background: "var(--erp-accent)",
              color: "var(--erp-accentFg)",
            }}
          >
            S
          </div>
          <span className="font-serif-display tracking-tight hidden sm:inline">
            {schoolName || "School ERP"}
          </span>
        </div>

        <div className="h-5 w-px bg-white/15 mx-1 hidden md:block" />

        {/* Branch switcher */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="branch-switcher"
              className="hidden md:flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10 text-[12px]"
            >
              <span className="opacity-70">Branch:</span>
              <span className="font-semibold">{branch?.name || "—"}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[180px]">
            <DropdownMenuLabel>Switch Branch</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {branches.map((b) => (
              <DropdownMenuItem
                key={b.id}
                data-testid={`branch-opt-${b.code}`}
                onClick={() => updateBranch(b.id)}
              >
                {b.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Session switcher */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="session-switcher"
              className="hidden md:flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10 text-[12px]"
            >
              <span className="opacity-70">Session:</span>
              <span className="font-semibold">{session?.name || "—"}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[180px]">
            <DropdownMenuLabel>Switch Session</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {sessions.map((s) => (
              <DropdownMenuItem
                key={s.id}
                data-testid={`session-opt-${s.name}`}
                onClick={() => {
                  if (String(s.id) === String(sessionId)) return;
                  setPendingSessionId(String(s.id));
                  setSessionConfirmOpen(true);
                }}
              >
                {s.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex-1" />

        {/* Theme picker (quick) */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              className="hidden md:flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10 text-[12px]"
              data-testid="theme-picker"
            >
              <Palette className="w-3.5 h-3.5" />
              <span className="hidden lg:inline">Theme</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[260px]">
            <DropdownMenuLabel>Choose Theme</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="grid grid-cols-1 gap-1 p-1">
              {Object.entries(THEMES).map(([k, t]) => (
                <button
                  key={k}
                  data-testid={`theme-opt-${k}`}
                  onClick={() => updateTheme(k)}
                  className={`flex items-center gap-2 p-2 rounded text-left ${themeKey === k ? "bg-slate-100" : "hover:bg-slate-50"}`}
                >
                  <div
                    className="w-8 h-8 rounded-md flex"
                    style={{ background: t.gradient }}
                  >
                    <div
                      className="w-3 h-full self-end"
                      style={{ background: t.accent }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold">{t.label}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {t.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Font size adjuster */}
        <div
          className="hidden md:flex items-center gap-1 text-[12px]"
          data-testid="font-size-control"
        >
          <button
            onClick={() => setFontSize((v) => Math.max(11, v - 1))}
            className="w-6 h-6 rounded hover:bg-white/10 flex items-center justify-center"
            data-testid="font-minus"
          >
            <Minus className="w-3 h-3" />
          </button>
          <span className="w-5 text-center font-mono text-[10px] opacity-80">
            {fontSize}
          </span>
          <button
            onClick={() => setFontSize((v) => Math.min(18, v + 1))}
            className="w-6 h-6 rounded hover:bg-white/10 flex items-center justify-center"
            data-testid="font-plus"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>

        {/* Profile */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/10"
              data-testid="profile-trigger"
            >
              <Avatar className="w-7 h-7 ring-2 ring-white/20">
                <AvatarFallback
                  style={{
                    background: "var(--erp-accent)",
                    color: "var(--erp-accentFg)",
                  }}
                  className="text-[11px] font-bold"
                >
                  {(user?.name || "U").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="text-left text-[12px] leading-tight hidden sm:block">
                <div className="font-semibold">
                  {user?.name?.split(" ")[0] || "User"}
                </div>
                <div className="opacity-70 text-[10px] uppercase">
                  {user?.role}
                </div>
              </div>
              <ChevronDown className="w-3 h-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setPwOpen(true)}
              data-testid="menu-change-pw"
            >
              <KeyRound className="w-4 h-4 mr-2" /> Change Password
            </DropdownMenuItem>
            {isSuperAdmin && (
              <DropdownMenuItem
                onClick={() => navigate("/settings")}
                data-testid="menu-settings"
              >
                <Settings className="w-4 h-4 mr-2" /> Settings & Themes
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              data-testid="menu-logout"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      <AlertDialog
        open={sessionConfirmOpen}
        onOpenChange={setSessionConfirmOpen}
      >
        <AlertDialogContent className="max-w-md border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl text-slate-900">
              Change session?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-slate-600">
              Do you really want to change session? This will reload module data
              for the selected session.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setPendingSessionId("");
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingSessionId) {
                  updateSession(pendingSessionId);
                }
                setPendingSessionId("");
              }}
              className="erp-primary"
            >
              Yes, Change Session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mega-menu nav (desktop only) */}
      <div
        className="hidden lg:block sticky top-12 z-40 erp-bg-nav border-b"
        style={{ borderColor: "var(--erp-border)" }}
      >
        <nav
          ref={megaNavRef}
          className="h-10 flex items-center overflow-x-auto px-2"
          data-testid="mega-nav"
          onWheel={(e) => {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
              e.preventDefault();
              e.currentTarget.scrollBy({ left: e.deltaY, behavior: "auto" });
              syncMegaNavScrollState();
            }
          }}
        >
          {visibleModules.map((m) => {
            const Icon = m.icon;
            return (
              <NavLink
                key={m.key}
                to={m.path}
                end={m.path === "/"}
                data-testid={`nav-${m.key}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 h-full text-[12px] font-semibold whitespace-nowrap border-b-2 transition-all ${
                    isActive
                      ? "erp-text-primary"
                      : "border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`
                }
                style={({ isActive }) =>
                  isActive ? { borderColor: "var(--erp-accent)" } : {}
                }
              >
                <Icon className="w-3.5 h-3.5" />
                {m.label}
              </NavLink>
            );
          })}
        </nav>

        {canScrollLeft && (
          <div
            className="absolute left-0 top-0 h-10 w-10 flex items-center justify-start pl-1"
            style={{
              background:
                "linear-gradient(to right, var(--erp-nav) 55%, transparent)",
            }}
            onMouseEnter={() => setAutoScrollDir(-1)}
            onMouseLeave={() => setAutoScrollDir(0)}
          >
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 rounded-full"
              onClick={() => {
                megaNavRef.current?.scrollBy({
                  left: -220,
                  behavior: "smooth",
                });
                syncMegaNavScrollState();
              }}
              aria-label="Scroll menu left"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
          </div>
        )}

        {canScrollRight && (
          <div
            className="absolute right-0 top-0 h-10 w-10 flex items-center justify-end pr-1"
            style={{
              background:
                "linear-gradient(to left, var(--erp-nav) 55%, transparent)",
            }}
            onMouseEnter={() => setAutoScrollDir(1)}
            onMouseLeave={() => setAutoScrollDir(0)}
          >
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 rounded-full"
              onClick={() => {
                megaNavRef.current?.scrollBy({ left: 220, behavior: "smooth" });
                syncMegaNavScrollState();
              }}
              aria-label="Scroll menu right"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      <main
        className="w-full max-w-[1600px] mx-auto px-2 md:px-3 lg:px-4 py-3"
        data-testid="main-content"
      >
        <Outlet />
      </main>
    </div>
  );
}
